<template>
  <div class="modal-wrapper">
    <div class="modal-dialog modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <button class="close" aria-label="Close" @click="$emit('close')">
            <span aria-hidden="true">&times;</span>
          </button>
          <h4 class="modal-title">
            Subir parcelamento
          </h4>
        </div>
        <div class="modal-body">
          <div v-if="errorMessage" class="alert alert-danger js-error">
            {{ errorMessage }}
          </div>

          <div v-if="existingAgreements.length" class="alert alert-warning js-existing">
            <strong>Esta cobrança já tem parcelamento subido.</strong>
            <ul>
              <li v-for="agreement in existingAgreements" :key="agreement.id">
                {{ agreement.agreement_code }} — {{ agreement.installments }} parcelas,
                total {{ agreement.full_value | formatCurrency }}
              </li>
            </ul>
            Confira os contratos desta cobrança antes de subir de novo, para o aluno não ser cobrado duas vezes.
          </div>

          <div class="row">
            <div class="col-md-6">
              <label>Planilha da faculdade</label>
              <input type="file" accept=".csv,text/csv,text/plain" class="js-file" @change="onFileChange">
              <p class="help-block">
                Três colunas: parcela, vencimento e valor. Aceita 10/03/2026 e 2026-03-10,
                1.240,00 e 1240.00.
              </p>
            </div>
            <div class="col-md-6">
              <label>Não tem a planilha no formato?</label>
              <p>
                <button class="btn btn-link js-template" @click="downloadTemplate">
                  Baixar modelo de planilha
                </button>
              </p>
              <p class="help-block">
                Abra no Excel, substitua pelas parcelas da faculdade e salve como CSV.
              </p>
            </div>
          </div>

          <div class="row" v-if="!file">
            <div class="col-md-12">
              <label>Ou cole a lista</label>
              <textarea
                class="form-control js-pasted" rows="4" v-model="pastedText"
                placeholder="1;10/03/2026;137,45"
              ></textarea>
            </div>
          </div>

          <div class="agreement-actions">
            <button class="btn btn-default js-check" :disabled="!hasEntry" @click="check">
              Conferir
            </button>
            <span v-if="rows.length" class="agreement-summary js-summary">
              <strong>{{ rows.length }}</strong> parcelas,
              total <strong>{{ total | formatCurrency }}</strong>,
              último vencimento <strong>{{ lastDueDate }}</strong>
            </span>
            <span v-if="rows.length && !previewValid" class="text-warning js-stale">
              A lista mudou desde a última conferência. Confira de novo antes de subir.
            </span>
          </div>

          <table class="table table-striped" v-if="rows.length">
            <thead>
              <tr>
                <th>#</th>
                <th>Vencimento</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in rows" :key="index">
                <td>{{ row.number }}</td>
                <td>
                  <input
                    type="text" class="form-control js-due-date" v-model="row.due_date"
                    @input="previewValid = false"
                  >
                </td>
                <td>
                  <input
                    type="text" class="form-control js-value" v-model="row.value"
                    @input="previewValid = false"
                  >
                </td>
              </tr>
            </tbody>
          </table>

          <div class="modal-footer">
            <small class="text-muted js-code">
              Código deste acordo: {{ agreementCode }}
            </small>
            <button
              class="btn btn-primary js-submit" :disabled="!previewValid || !rows.length"
              @click="submit"
            >
              Subir acordo
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import AgreementContractsService from 'services/AgreementContractsService';

// O nome inteiro cabe na maioria das instituições, que são marcas de uma palavra.
// O corte existe para o nome comprido não empurrar o número para fora da linha.
const MAX_PREFIX_LENGTH = 20;
const FALLBACK_PREFIX = 'ACORDO';

// O formato que o leitor do backend entende, com um exemplo de cada variação que
// a faculdade costuma mandar: data com barra, valor com milhar e valor sem.
const TEMPLATE_ROWS = [
  'parcela;vencimento;valor',
  '1;10/03/2026;1.240,00',
  '2;10/04/2026;1240,00',
  '3;10/05/2026;980,55',
];

export default {
  props: {
    billing: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      pastedText: '',
      file: null,
      rows: [],
      // A subida só libera com a lista aprovada pela conferência: editar uma linha
      // ou levar uma recusa derruba isso, para não cobrar número que o backend
      // acabou de recusar nem número que ninguém conferiu.
      previewValid: false,
      lastDueDate: null,
      errorMessage: null,
    };
  },
  computed: {
    ...mapState([
      'studentContracts',
    ]),

    // O que já foi subido nesta cobrança. Com o código gerado em vez de digitado,
    // é este aviso que impede a segunda subida da mesma planilha: a operação vê
    // o bloco na tela antes de apertar o botão.
    existingAgreements() {
      return (this.studentContracts || [])
        .filter(contract => contract.billing_id === this.billing.id && contract.agreement_code);
    },

    // A operação não digita o código: a faculdade manda a planilha sem nenhum, e
    // um código inventado na hora não serve nem para achar o acordo nem para
    // barrar a segunda cobrança. O número é livre DENTRO da cobrança, que é o
    // alcance do índice único no banco.
    agreementCode() {
      const taken = this.existingAgreements
        .map(contract => new RegExp(`^${this.codePrefix}-(\\d+)$`).exec(contract.agreement_code))
        .filter(Boolean)
        .map(match => Number(match[1]));

      return `${this.codePrefix}-${taken.length ? Math.max(...taken) + 1 : 1}`;
    },

    codePrefix() {
      const name = String(this.billing.institution_name || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase()
        .slice(0, MAX_PREFIX_LENGTH);
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');

      return `${name || FALLBACK_PREFIX}-${today.getFullYear()}-${month}`;
    },

    hasEntry() {
      return Boolean(this.file) || this.pastedText.trim().length > 0 || this.rows.length > 0;
    },

    // Shown live while the operator fixes a line, so the number she is about to
    // charge is always on screen. The backend recomputes it on every request.
    total() {
      return this.rows.reduce((sum, row) => sum + this.toNumber(row.value), 0);
    },
  },
  methods: {
    ...mapActions([
      'loadStudentContracts',
    ]),

    onFileChange(event) {
      const [file] = event.target.files;
      this.file = file || null;
    },

    // O modelo é montado aqui em vez de virar arquivo servido: são quatro linhas,
    // e assim ele nunca sai do passo com o formato que o backend aceita.
    downloadTemplate() {
      const blob = new Blob([`${TEMPLATE_ROWS.join('\n')}\n`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = 'modelo-parcelamento.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    // A pasted list travels as a file so that typing and uploading go through the
    // same reader on the backend, with the same rules for date and amount.
    entryPayload() {
      if (this.rows.length) {
        return { billing_id: this.billing.id, installments: this.rows };
      }

      const payload = new FormData();
      payload.append('billing_id', this.billing.id);
      payload.append('file', this.file || new Blob([this.pastedText], { type: 'text/csv' }), 'acordo.csv');

      return payload;
    },

    check() {
      this.errorMessage = null;
      this.$loader.show();

      return AgreementContractsService.preview(this.entryPayload())
        .then((data) => {
          this.rows = data.installments;
          this.lastDueDate = data.last_due_date;
          this.previewValid = true;
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message;
          // A grade fica: a recusa aponta uma linha, e apagar tudo faria a operação
          // colar as 100 de novo por causa de um valor.
          this.previewValid = false;
        })
        .finally(() => this.$loader.hide());
    },

    submit() {
      this.errorMessage = null;
      this.$loader.show();

      const payload = {
        billing_id: this.billing.id,
        agreement_code: this.agreementCode,
        installments: this.rows,
      };

      return AgreementContractsService.createAgreement(payload)
        .then((data) => {
          this.loadStudentContracts();
          this.$alert.notifySuccess(data.created ? 'Parcelamento subido' : 'Esse acordo já estava subido');
          this.$emit('close');
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message;
        })
        .finally(() => this.$loader.hide());
    },

    toNumber(value) {
      const text = String(value).trim();
      const normalized = /,\d{1,2}$/.test(text) ? text.replace(/\./g, '').replace(',', '.') : text;

      return parseFloat(normalized) || 0;
    },
  },
};
</script>

<style scoped>
.agreement-actions {
  margin: 15px 0;
}

.agreement-summary {
  margin-left: 15px;
}

.js-code {
  float: left;
  line-height: 34px;
}
</style>
